/**
 * PreferencesProvider — single source of truth for user preferences across
 * the SPA, AND the thing that actually applies them to the DOM.
 *
 * Why a provider (not just a hook):
 *  - settings UIs and consumer components (sidebar, dashboard, format
 *    helpers) all read the same value, so React Context avoids prop
 *    drilling and keeps everything in lock-step.
 *  - the live-DOM application (toggle `.dark`, `.compact`,
 *    `.no-animations` on <html>) happens here exactly once, regardless
 *    of how many components subscribe.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import i18n from '@/src/i18n'
import {
  DEFAULT_PREFERENCES,
  loadPreferences,
  savePreferences,
  type UserPreferences,
} from './preferences'

interface PreferencesContextValue {
  preferences: UserPreferences
  setPreference: <K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K],
  ) => void
  reset: () => void
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null)

/** Resolve `theme: 'system'` to a concrete `light | dark` value. */
function resolveTheme(theme: UserPreferences['theme']): 'light' | 'dark' {
  if (theme !== 'system') return theme
  if (typeof window === 'undefined' || !window.matchMedia) return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}

function applyToDom(prefs: UserPreferences) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.classList.toggle('dark', resolveTheme(prefs.theme) === 'dark')
  root.classList.toggle('compact', prefs.density === 'compact')
  root.classList.toggle('no-animations', !prefs.animationsEnabled)
  // Mirror the language onto <html lang=""> for screen readers + SEO.
  root.setAttribute('lang', prefs.language)
}

export function PreferencesProvider({ children }: { children: React.ReactNode }) {
  const [preferences, setPreferences] = useState<UserPreferences>(
    () => loadPreferences(),
  )

  // Apply on mount + whenever a preference flips. Cheap operation; React
  // batches the state update so this fires once per user action.
  useEffect(() => {
    applyToDom(preferences)
    savePreferences(preferences)
    // i18n is initialised before the provider mounts; this only emits a
    // language-changed event when the value actually differs.
    if (i18n.language !== preferences.language) {
      void i18n.changeLanguage(preferences.language)
    }
  }, [preferences])

  // Follow the OS theme live while the user is on "system".
  useEffect(() => {
    if (preferences.theme !== 'system') return
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => applyToDom(preferences)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [preferences])

  const setPreference = useCallback(
    <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => {
      setPreferences((prev) => ({ ...prev, [key]: value }))
    },
    [],
  )

  const reset = useCallback(() => {
    setPreferences(DEFAULT_PREFERENCES)
  }, [])

  const value = useMemo<PreferencesContextValue>(
    () => ({ preferences, setPreference, reset }),
    [preferences, setPreference, reset],
  )

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  )
}

export function usePreferences() {
  const ctx = useContext(PreferencesContext)
  if (!ctx) {
    throw new Error('usePreferences must be used inside PreferencesProvider')
  }
  return ctx
}
